import { FC, useMemo, useState } from "react";
import { OrganizationAdminQuantitiesExplainWorkspace } from "@common/schemas/organization-admin.schema";
import { OrganizationBillingClientsViewSchema } from "@common/types/views/organizationBillingClientsView";
import { View } from "@common/types/views/views";
import { Loader } from "@design-system/Loader/Loader";
import { Tabs } from "@design-system/Tabs/Tabs";
import { ViewTable } from "@design-system/ViewTable/ViewTable";
import { useActiveOrganization } from "@hooks/useActiveOrganization";
import { useOrganizationQuantitiesExplainQuery } from "@api/billingOrganization.api";
import { pluralizeWithCount } from "@common/helpers/string";
import { BillingPageV2InvoiceHistory } from "./BillingPageV2InvoiceHistory";
import {
    billingClientsViewConfig,
    buildBillingClientsRows,
    createBillingClientsView,
} from "./billingClientsViewConfig";
import styles from "./BillingPageV2ClientsSection.module.scss";

type BillingDetailsTab = "clients" | "history";

export const BillingPageV2ClientsSection: FC = () => {
    const activeOrganization = useActiveOrganization();
    const [tab, setTab] = useState<BillingDetailsTab>("clients");
    const hasSubscription = !!activeOrganization?.billing?.subscriptionId;
    const { data, isLoading, isError } = useOrganizationQuantitiesExplainQuery(
        activeOrganization?.id,
        hasSubscription,
    );
    const invoicedWorkspaces = useMemo(
        () =>
            data?.workspaces.filter(
                (workspace) => workspace.licenseStatus !== "excluded",
            ),
        [data],
    );

    return (
        <Tabs.Root
            value={tab}
            onValueChange={(value) => {
                if (value === "clients" || value === "history") {
                    setTab(value);
                }
            }}
            className={styles.container}
        >
            <Tabs.List className={styles.tabsList}>
                <Tabs.Tab value="clients">Clients on this invoice</Tabs.Tab>
                <Tabs.Tab value="history">Historical invoices</Tabs.Tab>
            </Tabs.List>
            <Tabs.Panel value="clients" className={styles.cardContainer}>
                <ClientsBody
                    isLoading={isLoading}
                    isError={isError || !hasSubscription}
                    workspaces={invoicedWorkspaces}
                />
            </Tabs.Panel>
            <Tabs.Panel value="history" className={styles.cardContainer}>
                <BillingPageV2InvoiceHistory
                    organizationId={activeOrganization?.id}
                    isActive={tab === "history"}
                />
            </Tabs.Panel>
        </Tabs.Root>
    );
};

interface ClientsBodyProps {
    isLoading: boolean;
    isError: boolean;
    workspaces: OrganizationAdminQuantitiesExplainWorkspace[] | undefined;
}

const ClientsBody: FC<ClientsBodyProps> = ({
    isLoading,
    isError,
    workspaces,
}) => {
    const [view, setView] = useState(createBillingClientsView);

    const rows = useMemo(
        () => (workspaces ? buildBillingClientsRows(workspaces) : []),
        [workspaces],
    );

    const handleViewChange = (nextView: View) => {
        setView(OrganizationBillingClientsViewSchema.parse(nextView));
    };

    if (isLoading) {
        return <Loader />;
    }

    if (isError || !workspaces) {
        return (
            <div className={styles.empty}>
                Client billing breakdown is unavailable.
            </div>
        );
    }

    if (workspaces.length === 0) {
        return (
            <div className={styles.empty}>
                No billable clients yet. Add a client to see how seats are
                allocated.
            </div>
        );
    }

    const billedCount = workspaces.filter(
        (workspace) =>
            workspace.licenseStatus === "billed" ||
            workspace.licenseStatus === "custom",
    ).length;
    const complimentaryCount = workspaces.filter(
        (workspace) => workspace.licenseStatus === "complimentary",
    ).length;

    return (
        <>
            <div className={styles.summary}>
                <span>
                    {pluralizeWithCount("client", workspaces.length)} ·{" "}
                    {pluralizeWithCount("billed", billedCount)} ·{" "}
                    {pluralizeWithCount("complimentary", complimentaryCount)}
                </span>
            </div>
            <ViewTable
                data={rows}
                view={view}
                config={billingClientsViewConfig}
                onViewChange={handleViewChange}
                getRowId={(row) => row.id}
                rowBorders={false}
                outerBorder={false}
                enableColumnResizing
                nestingIndentPx={16}
            />
        </>
    );
};
